import CloseIcon from "@mui/icons-material/Close";
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import { paymentManagementStatusOptions } from "Common/Components/StatusDropdown/statusOptions";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { exportServicePaymentManagementCSV, fetchServicePaymentManagementByType } from "slices/paymentmanagement/thunk";
// import FollowupModel from "pages/PaymentManagement/FollowupModel";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import FollowupModel from "components/FollowUpModal/FollowUpModel";
import FollowUpPopUp from "components/FollowUpModal/FollowUpPopUp";
import { SALES_ORDER_CATEGORY, SALES_ORDER_TYPE } from "Constants/StatusConstant";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import usePermission from "hooks/usePermission";
import DOMPurify from "dompurify";
import Chip from "components/Chips/Chip";
import { hideTagsSearchListingSection } from "pages/TagsSearchPage/utils/tagsSearchEmpty";

type ServiceOrderPaymentProps = {
  activeTab: string;
  noWrapper?: boolean;
  forceShowOnTagsSearch?: boolean;
};
const ServiceOrderPayment: React.FC<ServiceOrderPaymentProps> = ({
  activeTab,
  noWrapper = false,
  forceShowOnTagsSearch = false,
}) => {
  document.title = "Payment Management";

  // 🔍 If opened from /tags-search/:tags, use tag from URL as effective search
  const { tags: tagsParam } = useParams<{ tags?: string }>();
  const tagFromUrl = useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);

  const tableState = usePreservedTableState({
    key: TableKeys.PAYMENT_MANAGEMENT.SERVICE.ORDER,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableInstance, setTableInstance] = useState<any>();
  const [showFollowUpModal, setShowFollowUpModal] = useState<boolean>(false);
  const [selectedFollowUpItem, setSelectedFollowUpItem] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // When on tags-search route, always use URL tag as the search term
  const effectiveSearch = tagFromUrl || search;

  // Sync URL tag into search box once when present
  useEffect(() => {
    if (!tagFromUrl) return;
    setSearchInput(tagFromUrl);
    setSearch(tagFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFromUrl]);

  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.payment_management?.app_name,
    app_modules?.payment_management?.modules?.service
  );
  // const canUpdate = true

  const handleFetchPaymentManagement = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sort = sorting[0]?.id;
      const order = sorting[0]?.desc ? "desc" : "asc";

      const response = await dispatch(
        fetchServicePaymentManagementByType({
          activeTab: activeTab || "Service Payment",
          page: pageIndex + 1,
          pageSize,
          sort,
          search: effectiveSearch,
          ...(tagFromUrl ? { type: "tag" } : {}),
          order,
          filters: filterValue,
        })
      ).unwrap();
      setData(response.data);
      setTotalRecords(response.rowCount ?? 0);
    } catch (error: any) {
      console.error("Error fetching payment management data:", error);
      setError(error.message || "An error occurred");
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [dispatch, activeTab, pageIndex, pageSize, sorting, filterValue, effectiveSearch]);

  useEffect(() => {
    handleFetchPaymentManagement();
  }, [handleFetchPaymentManagement]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const handleStatusChange = async (
    salesOrderId: string,
    newStatus: string
  ) => {
    // await dispatch(
    // fetchSalesOrderStatusUpdate({ id: salesOrderId, status: newStatus })
    // ); have to add thunk to change status update for payment
    console.log("salesOrderId", salesOrderId);
    console.log("newStatus", newStatus);
  };

  const columns = useMemo(() => {
    return [
      // {
      //   header: "Sr.No",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => row.original.sr_no,
      // },
      {
        header: "SVR No.",
        accessorKey: "sales_order_number",
        enableSorting: false,
        enableColumnFilter: false,
        filterFetchUrl: API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
          "sales_order_number"
        ),
        dropdownMap: (data: any[]) => data.map(item => ({ label: item.label, value: item.value })),
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => row.original.sales_order_number ?? "-",
      },
      {
        header: "Service Number",
        accessorKey: "service_number",
        enableColumnFilter: false,
        enableSorting: false,
        filterFetchUrl: API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
          "service_number"
        ),
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => row.original.service_number ?? "-",
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterFetchUrl: API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
          "customer_name"
        ),
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => row.original.customer_name ?? "-",
      },
      {
        header: "Total Order Amount",
        accessorKey: "total_amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const symbol = row.original.currency_symbol || "₹";
          return `${symbol} ${formatIndianAmount(row.original.total_amount)}`;
        },
      },
      {
        header: "Received Amount",
        accessorKey: "received_amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const symbol = row.original.currency_symbol || "₹";
          return `${symbol} ${formatIndianAmount(row.original.received_amount)}`;
        },
      },
      {
        header: "Pending Amount",
        accessorKey: "pending_amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const symbol = row.original.currency_symbol || "₹";
          return `${symbol} ${formatIndianAmount(row.original.pending_amount)}`;
        },
      },
      {
        header: "Status",
        accessorKey: "order_payment_status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
          "order_payment_status"
        ),
        cell: ({ row }: { row: any }) => {
          const statusValue = row.original.order_payment_status;
          return <StatusChip status={statusValue || "Unpaid"} />;
        },
      },
      // {
      //   header: "Remark",
      //   accessorKey: "remark",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }: { row: any }) => row.original.remark ?? "N/A",
      // },
      {
        header: "Last Follow Up Date",
        accessorKey: "follow_up_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
          "follow_up_date"
        ),
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.follow_up_date} />
        ),
      },
      {
        header: "Follow Up Remark",
        accessorKey: "follow_up_remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const rawHtml = row.original?.follow_up_remark || "-";

          if (!rawHtml) return <span>-</span>;

          const safeHtml = DOMPurify.sanitize(rawHtml);

          return (
            <Tooltip
              arrow
              placement="top"
              title={
                <div
                  style={{
                    maxWidth: "450px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 500,
                    p: 1.5,
                  },
                },
              }}
            >
              <div
                style={{
                  minWidth: "200px",
                  maxWidth: "400px",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  lineHeight: "1.5",
                  maxHeight: "6em", // 4 lines * 1.5 line-height
                  cursor: "pointer",
                }}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </Tooltip>
          );
        },
      },
      {
        header: "Next Follow Up Date",
        accessorKey: "next_follow_up_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
          "next_follow_up_date"
        ),
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.next_follow_up_date} />
        ),
      },
      // {
      //   header: "Assigned To",
      //   accessorKey: "assigned_members",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => {
      //     const members = row.original?.assigned_members || [];

      //     if (!members.length) return "-";

      //     const names = members.map(
      //       (m: any) =>
      //         `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
      //     );

      //     const visible = names.slice(0, 4);
      //     const remaining = names.length - visible.length;

      //     return (
      //       <Tooltip
      //         arrow
      //         placement="top"
      //         title={names.join("\n")}
      //         componentsProps={{
      //           tooltip: {
      //             sx: {
      //               whiteSpace: "pre-line",
      //               maxWidth: 250,
      //               fontSize: "13px",
      //             },
      //           },
      //         }}
      //       >
      //         <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
      //           {visible.map((name: string, index: number) => (
      //             <div key={index}>{name}</div>
      //           ))}

      //           {remaining > 0 && (
      //             <div style={{ fontSize: "12px", color: "#6c757d" }}>
      //               +{remaining} more
      //             </div>
      //           )}
      //         </div>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
          "created_at"
        ),
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          return (
            <NameWithDate name={user?.username} date={row.original.created_at} />
          );
        },
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT?.PAYMENT_MANAGEMENT?.SERVICE?.FILTER_DROPDOWN(
          "updated_at"
        ),
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          return (
            <NameWithDate name={user?.username} date={row.original.updated_at} />
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          const menuItems = [
            {
              icon: <PaymentsIcon fontSize="small" color="primary" />,
              label: "Record Payment",
              permission: canUpdate,
              onClick: (item: any) => {
                navigate(`/payment-management/service/order/record-payment/${data.sales_order_service_id}`);
              },
              color: "primary",
            },
            {
              icon: <PersonAddIcon fontSize="small" color="success" />,
              label: "Follow-Up",
              permission: canUpdate,
              onClick: (item: any) => {
                setSelectedFollowUpItem(data);
                setShowFollowUpModal(true);
              },
              color: "primary",
            },
          ];
          return <DynamicMenuItem row={data} menuItems={menuItems} />;
        },
      },
    ].filter((column) => column.header !== "Action" || canUpdate);
  }, [navigate, canUpdate]);

  if (
    hideTagsSearchListingSection(
      noWrapper,
      loading,
      totalRecords,
      (data as any[])?.length ?? 0,
      error,
    ) &&
    !forceShowOnTagsSearch
  ) {
    return null;
  }

  return (
    <Fragment>
      <>
        <Card.Body>
          <LoaderErrorWrapper loading={loading} error={error}>
            <MasterTableContainer
              columns={columns}
              data={data || []}
              totalRecords={totalRecords}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              manualSorting={true}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
        </Card.Body>

        <Dialog
          open={showFollowUpModal}
          onClose={() => setShowFollowUpModal(false)}
          maxWidth={false}
          sx={{
            "& .MuiDialog-paper": {
              width: "95vw",
              maxWidth: "95vw",
              height: "100vh",
              borderRadius: "12px",
            },
          }}
          scroll="paper"
        >
          <DialogTitle sx={{ m: 0, p: 2, fontWeight: "bold", fontSize: "0.95rem", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            PBG Follow-up and Activities
            <IconButton
              aria-label="close"
              onClick={() => setShowFollowUpModal(false)}
              sx={{
                color: (theme) => theme.palette.grey[500],
                fontSize: "0.95rem",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            {selectedFollowUpItem && (
              <FollowUpPopUp
                open={showFollowUpModal}
                onClose={() => {
                  setShowFollowUpModal(false);
                  handleFetchPaymentManagement();
                }}
                title="Follow-up and Activities"
              >
                {selectedFollowUpItem && (
                  <FollowupModel
                    data={{
                      ...selectedFollowUpItem,
                      sales_order_service_id: selectedFollowUpItem.sales_order_service_id || selectedFollowUpItem.id,
                    }}
                    activeType={SALES_ORDER_TYPE?.SERVICE}
                    category={SALES_ORDER_CATEGORY?.ORDER}
                    onClose={() => {
                      setShowFollowUpModal(false)
                      handleFetchPaymentManagement()
                    }}
                    showAssignedMembers={false}
                  />
                )}
              </FollowUpPopUp>
            )}
          </DialogContent>
        </Dialog>
      </>
    </Fragment>
  );
};

export default ServiceOrderPayment;
